import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  setPage,
  setTotalItems,
  setTotalPages,
  setLimit,
} from "../redux/reducer/pagination/paginationSlice";
import {
  saveFilters,
  clearFilters,
} from "../redux/reducer/filterSlice/filterSlice";
import { ScreenFilterState } from "../redux/reducer/filterSlice/filterTypes";
import { useMessage } from "../context/MessageContext";
import { getApi, postApi, putApi } from "../apis/api";
import { useNavigate } from "react-router-dom";
import { GENERAL_ERROR_MESSAGE, VEHICLE_TYPE_MESSAGES } from "../utils/message";
import moment from "moment";
import { DEFAULT_PAGE_SIZE } from "../utils/constants";

interface useSearchAndFilterTypes {
  apiUrl: string;
  limit: number;
  mapUserData: (ApiResponse: any[]) => any[];
  extentedUrl?: string | "";
  customerUrlId?: string;
  agentHubUrlId?: string;
  fscLocationUrlId?: string;
  locationId?: string;
  tenantId?: string;
  xceleratorId?: string;
  dispatchCredId?: string;
  parentCustomerId?: string | "";
  customerIds?: string;
  agentHubIds?: string;
  locationName?: string;
  type?: string;
  invoiceGroupId?: string;
  isPostApi?: boolean;
  serviceId?: string;
  initialKeywords?: string;
  bindToGlobalPage?: boolean;
  screenKey?: string;
  activeTab?: string;
  shouldPublishPagination?: () => boolean;
  onPagination?: (p: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  }) => void;
  setCreateAgentPermission?: (value: boolean) => void;
}
export interface DropwdownInitailValueTypes {
  id: string;
  name: string;
}

const dropwdownInitailValue: DropwdownInitailValueTypes = {
  id: "",
  name: "",
};
interface Pagination {
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
  totalItems: number;
}
interface GetApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
  };
  pagination?: Pagination;
  checkAgentPermission?: boolean; // Add this line
}

interface columnData {
  key: string;
  label: string;
  id?: string;
}
/**
 * Custom hook for handling search, filter, pagination, and API interactions in a table or list.
 *
 * @template T - The type of data being fetched and managed.
 *
 * @param apiUrl - The URL to fetch data from.
 * @param limit - The maximum number of items to fetch.
 * @param mapUserData - Function to map the fetched data.
 *
 * @returns data - The fetched data for display in the table.
 * @returns setData - Function to manually set the fetched data.
 * @returns keyword - The current search keyword.
 * @returns currentPage - The current page number in the pagination.
 * @returns loading - Boolean indicating if data is being loaded.
 * @returns setLoading - Function to set the loading state.
 * @returns handleKeywordChange - Function to handle changes in the search input and debounce API calls.
 * @returns fetchFilteredData - Function to fetch data based on the current filters and pagination.
 * @returns handleSaveUpdate - Function to save or update data via API, with optional navigation.
 * @returns isShowFilterModal - Boolean indicating if the filter modal is currently shown.
 * @returns closeOpenfilterModal - Function to toggle the visibility of the filter modal.
 * @returns selectStatus - The currently selected status for filtering.
 * @returns setSelectStatus - Function to set the selected status for filtering.
 * @returns selectedRoles - The currently selected roles for filtering.
 * @returns setSelectedRoles - Function to set the selected roles for filtering.
 * @returns rolesDropdownData - The data used in the roles dropdown filter.
 * @returns setRolesDropdownData - Function to set the data used in the roles dropdown filter.
 * @returns orderStatusSelected - The currently selected order status for filtering.
 * @returns setOrderStatusSelected - Function to set the selected order status for filtering.
 * @returns orderTypeSelected - The currently selected order type for filtering.
 * @returns setOrderTypeSelected - Function to set the selected order type for filtering.
 * @returns agentTypeSelected - The currently selected order type for filtering.
 * @returns setAgentTypeSelected - Function to set the selected order type for filtering.
 * @returns hubSelected - The currently selected hub for filtering.
 * @returns setHubSelected - Function to set the selected hub for filtering.
 * @returns serviceSelected - The currently selected service for filtering.
 * @returns setServiceSelected - Function to set the selected service for filtering.
 * @returns orderStatusDropdownData - The data used in the order status dropdown filter.
 * @returns setOrderStatusDropdownData - Function to set the data used in the order status dropdown filter.
 * @returns orderTypeDropdownData - The data used in the order type dropdown filter.
 * @returns setOrderTypeDropdownData - Function to set the data used in the order type dropdown filter.
 * @returns agentTypeDropdownData - The data used in the order type dropdown filter.
 * @returns setAgentTypeDropdownData - Function to set the data used in the order type dropdown filter.
 * @returns hubDropdownData - The data used in the hub dropdown filter.
 * @returns setHubDropdownData - Function to set the data used in the hub dropdown filter.
 * @returns serviceDropdownData - The data used in the service dropdown filter.
 * @returns setServiceDropdownData - Function to set the data used in the service dropdown filter.
 */

// Screen keys that persist only items-per-page (paginationLimit) across navigations.
// Filters are NOT saved/restored for these screens — they reset on every visit.
const PAGINATION_ONLY_SCREENS = [
  "agentListRate",
  "customerGroupInvoice",
  // Agent additional charges persist filters per agent
  // (agentAdditionalCharges_<agentId>), so they are not listed here.
  "customerAdditionalCharges",
  // Tech Fee filters are per customer, like Additional Charges, so they must
  // not carry over to the next customer's Tech Fee tab.
  "customerTechFee",
];

const useSearchAndFilter = <T extends unknown>({
  apiUrl,
  limit = DEFAULT_PAGE_SIZE,
  mapUserData,
  extentedUrl = "",
  customerUrlId,
  agentHubUrlId,
  fscLocationUrlId,
  tenantId,
  xceleratorId,
  dispatchCredId,
  parentCustomerId,
  customerIds,
  agentHubIds,
  locationId,
  locationName,
  type,
  invoiceGroupId,
  isPostApi,
  serviceId,
  initialKeywords,
  screenKey,
  activeTab,
  bindToGlobalPage = true,
  shouldPublishPagination = () => true,
  onPagination,
  setCreateAgentPermission,
}: useSearchAndFilterTypes) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setMessage } = useMessage();
  const currentPage = useSelector(
    (state: RootState) => state.pagination.currentPage
  );
  const reduxLimit = useSelector((state: RootState) => state.pagination.limit);
  const filterState = useSelector((state: RootState) => state.filter);
  // Only restore filters if the last active screen matches (means user came back from a detail page)
  // Use a ref to capture initial values so they don't change on re-renders
  const initialFilterState = useRef(filterState);
  const preserveFiltersRef = useRef(false);
  const prevLimitRef = useRef(reduxLimit);
  const restoredFilters = useMemo(() => {
    const { savedFilters } = initialFilterState.current;
    if (screenKey && savedFilters?.[screenKey]) {
      if (PAGINATION_ONLY_SCREENS.includes(screenKey)) {
        // Pagination-only screen: restore only items-per-page, skip all filter values
        // so every filter state initializes to its default on mount.
        const { paginationLimit } = savedFilters[screenKey];
        return { paginationLimit } as ScreenFilterState;
      }
      return savedFilters[screenKey];
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Initialize with the last-applied snapshot from Redux (if filters were applied in a previous session).
  // This prevents dirty in-progress filter inputs from leaking into the saved state on navigate-back.
  const lastAppliedFiltersRef = useRef<Record<string, any> | null>(
    restoredFilters?.filterApplied ? restoredFilters : null
  );
  // Restore pagination from saved filters on mount (runs before first fetch).
  // If saved filters exist for this screen, use the saved limit/page.
  // Otherwise, use the default limit prop so the previous screen's global reduxLimit doesn't leak.
  const restoredLimitRef = useRef(restoredFilters?.paginationLimit ?? (screenKey ? limit : null));
  useEffect(() => {
    if (restoredFilters) {
      if (restoredFilters.paginationLimit) {
        dispatch(setLimit(restoredFilters.paginationLimit));
      }
    } else if (screenKey) {
      // No saved filters for this screen — reset global pagination
      // so it doesn't carry over from the previous screen.
      dispatch(setLimit(limit));
      dispatch(setPage(1));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [data, setData] = useState<T[]>([]);
  const [keyword, setKeyword] = useState<string>(initialKeywords || "");
  const [loading, setLoading] = useState<boolean>(false);
  const [originalPage, setOriginalPage] = useState<number>(1);
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  // State variables for handling filter modals and dropdown data
  const [isShowFilterModal, setIsShowFilterModal] = useState<boolean>(false);
  const [selectStatus, setSelectStatus] = useState<DropwdownInitailValueTypes>(
    restoredFilters?.selectStatus ?? dropwdownInitailValue
  );
  const [customerSelectedLevel, setCustomerSelectedLevel] =
    useState<DropwdownInitailValueTypes>(restoredFilters?.customerSelectedLevel ?? dropwdownInitailValue);
  const [orderStatusSelected, setOrderStatusSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.orderStatusSelected ?? []);
  const [customerTypeLevel, setCustomerTypeLevel] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.customerTypeLevel ?? []);

  const [orderTypeSelected, setOrderTypeSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.orderTypeSelected ?? []);
  const [handlingUnitSelected, setHandlingUnitSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.handlingUnitSelected ?? []);

  const [agentTypeSelected, setAgentTypeSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.agentTypeSelected ?? []);
  const [hubSelected, setHubSelected] = useState<DropwdownInitailValueTypes[]>(
    restoredFilters?.hubSelected ?? []
  );
  const [serviceSelected, setServiceSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.serviceSelected ?? []);
  const [agentUpdateStartDate, setAgentUpdateStartDate] = useState<string>(restoredFilters?.agentUpdateStartDate ?? "");
  const [agentUpdateEndDate, setAgentUpdateEndDate] = useState<string>(restoredFilters?.agentUpdateEndDate ?? "");
  const [dtsUpdateStartDate, setDtsStartDate] = useState<string>(restoredFilters?.dtsUpdateStartDate ?? "");
  const [dtsUpdateEndDate, setDtsEndDate] = useState<string>(restoredFilters?.dtsUpdateEndDate ?? "");
  const [createdStartDate, setCreatedStartDate] = useState<string>(restoredFilters?.createdStartDate ?? "");
  const [createdEndDate, setCreatedEndDate] = useState<string>(restoredFilters?.createdEndDate ?? "");
  const [invoiceFrom, setInvoiceFrom] = useState<string>(restoredFilters?.invoiceFrom ?? "");
  const [invoiceTo, setInvoiceTo] = useState<string>(restoredFilters?.invoiceTo ?? "");
  const [dueFrom, setDueFrom] = useState<string>(restoredFilters?.dueFrom ?? "");
  const [dueTo, setDueTo] = useState<string>(restoredFilters?.dueTo ?? "");
  const [orderDateFrom, setOrderDateFrom] = useState<string>(restoredFilters?.orderDateFrom ?? "");
  const [orderDateTo, setOrderDateTo] = useState<string>(restoredFilters?.orderDateTo ?? "");
  const [invoiceSetting, setInvoiceSetting] = useState<string>("");
  const [selectedRoles, setSelectedRoles] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.selectedRoles ?? []);
  const [rolesDropdownData, setRolesDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [orderStatusDropdownData, setOrderStatusDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [serviceLevelDropdownData, setServiceLevelDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [customerLevelDropdownData, setCustomerLevelDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [orderTypeDropdownData, setOrderTypeDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [handlingUnitDropDown, setHandlingUnitDropDown] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [agentTypeDropdownData, setAgentTypeDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [hubDropdownData, setHubDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [serviceDropdownData, setServiceDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [reportSchSelected, setReportSchSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.reportSchSelected ?? []);
  const [reportSchDropdownData, setReportSchDropDownData] = useState<
    DropwdownInitailValueTypes[]
  >([
    { name: "Daily", id: "daily" },
    { name: "Week", id: "week" },
    { name: "Month", id: "month" },
  ]);
  const [reportTypeSelected, setReportTypeSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.reportTypeSelected ?? []);
  const [reportCatSelected, setReportCatSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.reportCatSelected ?? []);
  const [reportTypeDropdownData, setReportTypeDropDownData] = useState<
    DropwdownInitailValueTypes[]
  >([
    { name: "All", id: "all" },
    { name: "Custom", id: "custom" },
  ]);
  const [reportCatDropdownData, setReportCatDropDownData] = useState<
    DropwdownInitailValueTypes[]
  >([
    { id: "monthEndReport", name: "Month End Report" },
    { id: "activeOrderReport", name: "Active Order Report" },
    { id: "historyOrderReport", name: "History Order Report" },
    { id: "invoiceOrderReport", name: "Invoice Order Report" },
    { id: "invoiceReport", name: "Invoice Report" },
  ]);
  const [reportDateTypeSelected, setReportDateTypeSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.reportDateTypeSelected ?? []);
  const [reportDateTypeDropdownData, setReportDateTypeDropDownData] = useState<
    DropwdownInitailValueTypes[]
  >([
    { id: "order date", name: "Order Date" },
    { id: "deliver date", name: "Delivered Status Date" },
    { id: "invoice date", name: "Invoice Date" },
  ]);
  const [paymentStatusSelected, setPaymentStatusSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.paymentStatusSelected ?? []);
  const [paymentStatusDropdownData, setPaymentStatusDropDownData] = useState<
    DropwdownInitailValueTypes[]
  >([
    { name: "Paid", id: "paid" },
    { name: "Unpaid", id: "unpaid" },
  ]);
  const [invoiceSettingSelected, setInvoiceSettingSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.invoiceSettingSelected ?? []);
  const [invoiceSettingDropdownData, setInvoiceSettingDropDownData] = useState<
    DropwdownInitailValueTypes[]
  >([
    { name: "Invoice Email", id: "invoiceEmail" },
    { name: "Invoice EDI", id: "invoiceEDI" },
  ]);
  const [filterApplied, setFilterApplied] = useState<boolean>(restoredFilters?.filterApplied ?? false);
  const [orderNumber, setOrderNumber] = useState<string>(restoredFilters?.orderNumber ?? "");
  const [consigneeName, setConsigneeName] = useState<string>(restoredFilters?.consigneeName ?? "");
  const [customerName, setCustomerName] = useState<string>(restoredFilters?.customerName ?? "");
  const [firstName, setFirstName] = useState<string>(restoredFilters?.firstName ?? "");
  const [lastName, setLastName] = useState<string>(restoredFilters?.lastName ?? "");
  const [agentName, setAgentName] = useState<string>(restoredFilters?.agentName ?? "");
  const [agentEmail, setAgentEmail] = useState<string>(restoredFilters?.agentEmail ?? "");
  const [carrierCode, setCarrierCode] = useState<string>(restoredFilters?.carrierCode ?? "");
  const [reportTitle, setReportTitle] = useState<string>(restoredFilters?.reportTitle ?? "");
  const [createdBy, setCreatedBy] = useState<string>(restoredFilters?.createdBy ?? "");
  const [invoiceGroup, setInvoiceGroup] = useState<string>(restoredFilters?.invoiceGroup ?? "");
  const [invoiceNumber, setInvoiceNumber] = useState<string>(restoredFilters?.invoiceNumber ?? "");
  const [invoiceEDI, setInvoiceEDI] = useState<DropwdownInitailValueTypes>(
    restoredFilters?.invoiceEDI ?? dropwdownInitailValue
  );
  const [invoiceEmail, setInvoiceEmail] = useState<DropwdownInitailValueTypes>(
    restoredFilters?.invoiceEmail ?? dropwdownInitailValue
  );
  const [orderRef, setOrderRef] = useState<string>(restoredFilters?.orderRef ?? "");
  const [admin, setAdmin] = useState<string>(restoredFilters?.admin ?? "");
  const [service, setService] = useState<string>(restoredFilters?.service ?? "");
  const [vehicleType, setVehicleType] = useState<string>(restoredFilters?.vehicleType ?? "");
  const [packageType, setPackageType] = useState<string>(restoredFilters?.packageType ?? "");
  const [hubName, setHubName] = useState<string>(restoredFilters?.hubName ?? "");
  const [templateName, setTemplateName] = useState<string>(restoredFilters?.templateName ?? "");
  const [billingAccountCode, setBillingAccountCode] = useState<string>(restoredFilters?.billingAccountCode ?? "");
  const [selectApiType, setSelectApiType] =
    useState<DropwdownInitailValueTypes>(restoredFilters?.selectApiType ?? dropwdownInitailValue);
  const [templateForSelected, setTemplateForSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.templateForSelected ?? []);
  const [templateForDropdownData, setTemplateForDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [city, setCity] = useState<string>(restoredFilters?.city ?? "");
  const [state, setState] = useState<string>(restoredFilters?.state ?? "");
  const [zipCode, setZipCode] = useState<string>(restoredFilters?.zipCode ?? "");
  const [address, setAddress] = useState<string>(restoredFilters?.address ?? "");
  const [description, setDescription] = useState<string>(restoredFilters?.description ?? "");
  const [scac, setScac] = useState<string>(restoredFilters?.scac ?? "");
  const [carrier, setCarrier] = useState<string>(restoredFilters?.carrier ?? "");

  const [region, setRegion] = useState<string>(restoredFilters?.region ?? "");
  const [onwardService, setOnwardService]=useState<string>(restoredFilters?.onwardService ?? "");
  const [stateOfRegion, setStateOfRegion] = useState<string>(restoredFilters?.stateOfRegion ?? "");
  const [serviceName, setServiceName] = useState<string>(restoredFilters?.serviceName ?? "");
  const [subCustomerName, setSubCustomerName] = useState<string>(restoredFilters?.subCustomerName ?? "");
  const [country, setCountry] = useState<string>(restoredFilters?.country ?? "");
  const [rateCharge, setRateCharge] = useState<string>(restoredFilters?.rateCharge ?? "");
  // Services list: single-select Service Type (delivery / pickup)
  const [logisticsTypeSelected, setLogisticsTypeSelected] =
    useState<DropwdownInitailValueTypes>(
      restoredFilters?.logisticsTypeSelected ?? { name: "", id: "" }
    );
  // My Rep List filters
  const [repName, setRepName] = useState<string>(restoredFilters?.repName ?? "");
  const [repCustomerSelected, setRepCustomerSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.repCustomerSelected ?? []);
  const [repCustomerDropdownData, setRepCustomerDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  const [commissionTypeSelected, setCommissionTypeSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.commissionTypeSelected ?? []);
  const [commissionTypeDropdownData, setCommissionTypeDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);
  // Audit Log "Module" filter — options come from the static-data-sourced
  // auditListModule list (see staticDataSlice), not a paginated API call.
  const [moduleSelected, setModuleSelected] = useState<
    DropwdownInitailValueTypes[]
  >(restoredFilters?.moduleSelected ?? []);
  const [moduleDropdownData, setModuleDropdownData] = useState<
    DropwdownInitailValueTypes[]
  >([]);

  /**
   * Fetches filtered data from the API based on the current page and keyword.
   * Updates the Redux state for pagination and maps the response data.
   *
   * @param {number} page - The page number to fetch.
   */
  const fetchFilteredData = async (
    page: number = 1,
    selectStatus?: DropwdownInitailValueTypes,
    selectedRoles?: DropwdownInitailValueTypes[],
    orderStatusSelected?: DropwdownInitailValueTypes[],
    orderTypeSelected?: DropwdownInitailValueTypes[],
    handlingUnitSelected?: DropwdownInitailValueTypes[],
    reportTypeSelected?: DropwdownInitailValueTypes[],
    reportCatSelected?: DropwdownInitailValueTypes[],
    reportDateTypeSelected?: DropwdownInitailValueTypes[],
    reportSchSelected?: DropwdownInitailValueTypes[],
    paymentStatusSelected?: DropwdownInitailValueTypes[],
    agentTypeSelected?: DropwdownInitailValueTypes[],
    hubSelected?: DropwdownInitailValueTypes[],
    serviceSelected?: DropwdownInitailValueTypes[],
    customerSelectedLevel?: DropwdownInitailValueTypes,
    customerTypeLevel?: DropwdownInitailValueTypes[],
    agentUpdateStartDate?: string,
    agentUpdateEndDate?: string,
    dtsUpdateStartDate?: string,
    dtsUpdateEndDate?: string,
    createdStartDate?: string,
    createdEndDate?: string,
    invoiceFrom?: string,
    invoiceTo?: string,
    dueFrom?: string,
    dueTo?: string,
    orderDateFrom?: string,
    orderDateTo?: string,
    selectApiType?: DropwdownInitailValueTypes,
    orderNumber?: string,
    consigneeName?: string,
    customerName?: string,
    firstName?: string,
    lastName?: string,
    agentName?: string,
    carrierCode?: string,
    reportTitle?: string,
    createdBy?: string,
    invoiceGroup?: string,
    invoiceEDI?: DropwdownInitailValueTypes,
    invoiceEmail?: DropwdownInitailValueTypes,
    invoiceNumber?: string,
    orderRef?: string,
    admin?: string,
    service?: string,
    vehicleType?: string,
    packageType?: string,
    hubName?: string,
    templateName?: string,
    templateForSelected?: DropwdownInitailValueTypes[],
    billingAccountCode?: string,
    invoiceSettingSelected?: DropwdownInitailValueTypes[],
    city?: string,
    state?: string,
    zipCode?: string,
    address?: string,
    description?: string,
    scac?: string,
    carrier?: string,
    region?: string,
    stateOfRegion?: string,
    onwardService?:string,
    serviceName?: string,
    agentEmail?: string,
    subCustomerName?: string,
    country?: string,
    rateCharge?: string,
    repName?: string,
    repCustomerSelected?: DropwdownInitailValueTypes[],
    commissionTypeSelected?: DropwdownInitailValueTypes[],
    logisticsTypeSelected?: DropwdownInitailValueTypes,
    moduleSelected?: DropwdownInitailValueTypes[],
  ) => {
    if (!apiUrl || extentedUrl.includes("undefined")) {
      return;
    }

    try {
      setLoading(true);
      // On the first fetch, prefer the restored per-screen limit over the global reduxLimit
      // (which may still hold a stale value from the previous screen).
      const restoredLimit = restoredLimitRef.current;
      let effectiveLimit: number;
      if (restoredLimit) {
        effectiveLimit = restoredLimit;
        restoredLimitRef.current = null;
      } else {
        effectiveLimit = (bindToGlobalPage ? reduxLimit : limit) ?? limit;
      }
      let url = `${apiUrl}?limit=${effectiveLimit}&page=${page}`;
      if (keyword.trim() !== "") {
        if (apiUrl && apiUrl.includes("combinelist")) {
          url += `&keyword=${keyword}`;
        } else if (initialKeywords?.trim()) {
          url += `&masterSearch=${initialKeywords}`;
        } else {
          url += `&keyword=${keyword}`;
        }
      }
      if (extentedUrl) {
        url += extentedUrl;
      }
      if (customerUrlId) {
        url += `&customerId=${customerUrlId}`;
      }
      if (orderNumber) {
        url += `&orderNumber=${orderNumber}`;
      }

      if (firstName) {
        url += `&firstName=${firstName}`;
      }
      if (lastName) {
        url += `&lastName=${lastName}`;
      }
      if (agentName) {
        // Decision D7 (Tech Fee): its list filters by agentName, like the charges lists.
        if (
          apiUrl &&
          (apiUrl.includes("/charges/") || apiUrl.includes("/customer-techfee"))
        ) {
          url += `&agentName=${agentName}`;
        } else {
          url += `&keyword=${agentName}`;
        }
      }
      if (agentEmail) {
        url += `&email=${agentEmail}`;
      }
      if (carrierCode) {
        url += `&carrierCode=${carrierCode}`;
      }
      if (orderRef) {
        url += `&orderRef=${orderRef}`;
      }
      if (admin) {
        url += `&admin=${admin}`;
      }
      if (service) {
        url += `&keyword=${service}`;
      }
      if (vehicleType) {
        url += `&keyword=${vehicleType}`;
      }
      if (packageType) {
        url += `&keyword=${packageType}`;
      }
      if (hubName) {
        // For location mapping endpoints, use keyword parameter instead of hubName
        if (
          apiUrl.includes("/location-mapping/") ||
          apiUrl.includes("/tenant-location")
        ) {
          url += `&keyword=${hubName}`;
        } else {
          // For other endpoints, keep the hubName parameter
          url += `&hubName=${hubName}`;
        }
      }
      if (consigneeName) {
        url += `&consigneeName=${consigneeName}`;
      }
      if (customerName) {
        url += `&customerName=${customerName}`;
      }
      if (subCustomerName) {
        url += `&subCustomerName=${subCustomerName}`;
      }
      if (reportTitle) {
        url += `&reportTitle=${reportTitle}`;
      }
      if (createdBy) {
        url += `&createdBy=${createdBy}`;
      }
      if (invoiceGroup) {
        url += `&invoiceGroup=${invoiceGroup}`;
      }
      if (invoiceNumber) {
        url += `&invoiceNo=${invoiceNumber}`;
      }
      if (invoiceEDI?.id) {
        url += `&invoiceEDI=${invoiceEDI.id}`;
      }
      if (invoiceEmail?.id) {
        url += `&invoiceEmail=${invoiceEmail.id}`;
      }
      if (agentHubUrlId) {
        url += `&agentHubId=${agentHubUrlId}`;
      }
      if (fscLocationUrlId) {
        url += `&fscLocationId=${fscLocationUrlId}`;
      }
      if (locationId) {
        url += `&locationId=${locationId}`;
      }
      if (locationName) {
        url += `&locationName=${locationName}`;
      }
      if (tenantId) {
        url += `&tenantId=${tenantId}`;
      }
      if (xceleratorId) {
        url += `&xceleratorId=${xceleratorId}`;
      }
      if (parentCustomerId) {
        url += `&parentCustomerId=${parentCustomerId}`;
      }
      if (invoiceGroupId) {
        url += `&invoiceGroupId=${invoiceGroupId}`;
      }
      if (selectStatus?.id) {
        url += `&status=${selectStatus.id}`;
      }
      if (customerSelectedLevel?.id) {
        url += `&customerType=${customerSelectedLevel.id}`;
      }
      if (selectedRoles && selectedRoles.length > 0) {
        url += `&roleIds=${selectedRoles?.map((role) => role.id)?.join(",")}`;
      }
      if (orderStatusSelected && orderStatusSelected.length > 0) {
        url = url.replace(/&statusTypeIds=[^&]*/g, "");
        url += `&statusTypeIds=${orderStatusSelected
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (orderTypeSelected && orderTypeSelected.length > 0) {
        url += `&orderTypeIds=${orderTypeSelected
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (handlingUnitSelected && handlingUnitSelected.length > 0) {
        url += `&unitType=${handlingUnitSelected
          ?.map((role) => role.name.toLowerCase())
          ?.join(",")}`;
      }
      if (agentTypeSelected && agentTypeSelected.length > 0) {
        url += `&agentTypeId=${agentTypeSelected
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (hubSelected && hubSelected.length > 0) {
        url += `&hubIds=${hubSelected?.map((role) => role.id)?.join(",")}`;
      }
      if (serviceSelected && serviceSelected.length > 0) {
        url += `&serviceIds=${serviceSelected
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (reportSchSelected && reportSchSelected.length > 0) {
        url += `&scheduling=${reportSchSelected
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (paymentStatusSelected && paymentStatusSelected.length > 0) {
        url += `&paymentStatus=${paymentStatusSelected
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (invoiceSettingSelected && invoiceSettingSelected.length > 0) {
        url += `&invoiceSetting=${invoiceSettingSelected
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (reportTypeSelected && reportTypeSelected.length > 0) {
        url += `&type=${reportTypeSelected?.map((role) => role.id)?.join(",")}`;
      }
      if (reportCatSelected && reportCatSelected.length > 0) {
        url += `&category=${reportCatSelected
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (reportDateTypeSelected && reportDateTypeSelected.length > 0) {
        url += `&reportDateType=${reportDateTypeSelected
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (
        agentUpdateStartDate !== "" &&
        agentUpdateStartDate !== undefined &&
        agentUpdateEndDate !== "" &&
        agentUpdateEndDate !== undefined
      ) {
        const startParam = moment(agentUpdateStartDate)
          .startOf("day")
          .utc()
          .format();
        const endDateParam = moment(agentUpdateEndDate)
          .endOf("day")
          .utc()
          .format();
        url += `&agentLastUpdateStartTime=${startParam}&agentLastUpdateEndTime=${endDateParam}`;
      }
      if (
        dtsUpdateStartDate !== "" &&
        dtsUpdateStartDate !== undefined &&
        dtsUpdateEndDate !== "" &&
        dtsUpdateStartDate !== undefined
      ) {
        const startParam = moment(dtsUpdateStartDate)
          .startOf("day")
          .utc()
          .format();
        const endDateParam = moment(dtsUpdateEndDate)
          .endOf("day")
          .utc()
          .format();
        url += `&dtsLastUpdateStartTime=${startParam}&dtsLastUpdateEndTime=${endDateParam}`;
      }
      if (
        createdStartDate !== "" &&
        createdStartDate !== undefined &&
        createdEndDate !== "" &&
        createdEndDate !== undefined
      ) {
        const startParam = moment(createdStartDate)
          .startOf("day")
          .utc()
          .format();
        const endDateParam = moment(createdEndDate).endOf("day").utc().format();
        url += `&createdDateStartTime=${startParam}&createdDateEndTime=${endDateParam}`;
      }
      if (
        invoiceFrom !== "" &&
        invoiceFrom !== undefined &&
        invoiceTo !== "" &&
        invoiceTo !== undefined
      ) {
        const startParam = moment(invoiceFrom).startOf("day").utc().format();
        const endDateParam = moment(invoiceTo).endOf("day").utc().format();
        url += `&invoiceFrom=${startParam}&invoiceTo=${endDateParam}`;
      }
      if (
        dueFrom !== "" &&
        dueFrom !== undefined &&
        dueTo !== "" &&
        dueTo !== undefined
      ) {
        const startParam = moment(dueFrom).startOf("day").utc().format();
        const endDateParam = moment(dueTo).endOf("day").utc().format();
        url += `&dueFrom=${startParam}&dueTo=${endDateParam}`;
      }
      if (
        orderDateFrom !== "" &&
        orderDateFrom !== undefined &&
        orderDateTo !== "" &&
        orderDateTo !== undefined
      ) {
        const startParam = moment(orderDateFrom).startOf("day").utc().format();
        const endDateParam = moment(orderDateTo).endOf("day").utc().format();
        url += `&orderDateFrom=${startParam}&orderDateTo=${endDateParam}`;
      }
      if (customerTypeLevel && customerTypeLevel.length > 0) {
        url += `&customerType=${customerTypeLevel
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (templateForSelected && templateForSelected.length > 0) {
        url += `&templateFor=${templateForSelected
          ?.map((role) => role.id)
          ?.join(",")}`;
      }
      if (customerIds) {
        url += `&customerIds=${customerIds}`;
      }
      if (agentHubIds) {
        url += `&agentHubIds=${agentHubIds}`;
      }
      if (serviceId) {
        url += `&serviceId=${serviceId}`;
      }
      if (type) {
        url += `&type=${type}`;
      }
      if (selectApiType?.id) {
        url += `&type=${selectApiType.id}`;
      }
      if (dispatchCredId) {
        url += `&credId=${dispatchCredId}`;
      }
      if (templateName) {
        url += `&templateName=${templateName}`;
      }
      if (billingAccountCode) {
        url += `&agentBillingCode=${billingAccountCode}`;
      }

      if (invoiceSetting) {
        url += `&invoiceSetting=${invoiceSetting}`;
      }
      if (city) {
        url += `&city=${city}`;
      }
      if (state) {
        url += `&state=${state}`;
      }
      if (zipCode) {
        url += `&zipCode=${zipCode}`;
      }
      if (address) {
        url += `&addressStr=${address}`;
      }
      if (description) {
        url += `&description=${description}`;
      }
      if (scac) {
        url += `&scac=${scac}`;
      }
      if (carrier) {
        url += `&name=${carrier}`;
      }
      if (region) {
        url += `&region=${region}`
      }
      if (stateOfRegion) {
        url += `&state=${stateOfRegion}`
      }
      if(onwardService){
          if(apiUrl.includes("service/order-status/")){
          url+=`&onwardStatusName=${onwardService}`
        }
        else{
          url+= `&onwardServiceName=${onwardService}`
        }
      }
      if(serviceName){
        url += `&keyword=${serviceName}`
      }
      if (country) {
        url += `&country=${country}`;
      }
      if (rateCharge) {
        url += `&rateChargeCode=${rateCharge}`;
      }
      if (repName) {
        url += `&name=${repName}`;
      }
      if (commissionTypeSelected && commissionTypeSelected.length > 0) {
        url += `&commissionType=${commissionTypeSelected
          ?.map((commission) => commission.id)
          ?.join(",")}`;
      }
      if (logisticsTypeSelected?.id) {
        url += `&logisticsType=${logisticsTypeSelected.id}`;
      }
      if (moduleSelected && moduleSelected.length > 0) {
        url += `&module=${moduleSelected.map((m) => m.id).join(",")}`;
      }
      // console.log(true, invoiceSetting);
      // if (invoiceSettingSelected) {
      //   url += `&invoiceSetting=${invoiceSettingSelected}`;
      // }
      const response = await getApi(url);

      if (response.success) {
        const userData: any = response.data || [];
        const paginationData = {
          totalItems: response.pagination?.totalItems || 0,
          totalPages: response.pagination?.totalPages || 1,
          currentPage: response.pagination?.currentPage || 1,
        };
        setCreateAgentPermission &&
          setCreateAgentPermission(
            (response as any)?.checkAgentPermission || false
          );
        paginationData.currentPage =
          paginationData.currentPage > paginationData.totalPages
            ? paginationData.totalPages
            : paginationData.currentPage;

        onPagination?.({
          totalItems: paginationData.totalItems,
          totalPages: paginationData.totalPages,
          currentPage: paginationData.currentPage,
          limit,
        });

        // Only publish to Redux if: (a) this hook is allowed to bind to global page
        // AND (b) the caller says “yes, publish this one” (prevents cross-talk).
        if (bindToGlobalPage && shouldPublishPagination()) {
          dispatch(setTotalItems(paginationData.totalItems));
          dispatch(setTotalPages(paginationData.totalPages));
          dispatch(setLimit(effectiveLimit));
          if (paginationData.currentPage !== currentPage) {
            dispatch(setPage(paginationData.currentPage));
          }
        }
        userData.forEach((item: any, index: number) => {
          item.count = (page - 1) * effectiveLimit + index + 1;
        });
        const mappedData = mapUserData(userData);
        setData(mappedData);
      } else {
        setMessage(response.error?.message || GENERAL_ERROR_MESSAGE, "error");
      }
    } catch (error) {
      console.log("error", error);
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles changes to the search keyword input and triggers a data fetch.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
   */
  const handleKeywordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e?.target?.value;
      setKeyword(value);

      if (!bindToGlobalPage) {
        // local-only: do not touch Redux pagination
        if (value.trim().length === 1) {
          setOriginalPage(currentPage); // keep local memory if needed
        }
        return;
      }

      // global pagination mode
      if (value.trim() === "") {
        dispatch(setPage(originalPage));
      } else if (value.trim().length === 1) {
        setOriginalPage(currentPage);
      }
    },
    [bindToGlobalPage, dispatch, originalPage, currentPage]
  );

  const setKeywordValue = useCallback(
    (value: string) => {
      setKeyword(value);

      if (!bindToGlobalPage) return;

      if (value.trim() === "") {
        dispatch(setPage(originalPage));
      } else if (value.trim().length === 1) {
        setOriginalPage(currentPage);
      }
    },
    [bindToGlobalPage, dispatch, originalPage, currentPage]
  );

  /**
   * Handles saving or updating data via API, with optional navigation upon success.
   *
   * @param {string} apiUrl - The API endpoint URL.
   * @param {any[]} apiPayload - The data to be sent to the API.
   * @param {string} type - The type of operation ("Create" or "Update").
   * @param {string} successMessage - The success message to display.
   * @param {string} navigationPath - The path to navigate to after success.
   */

  const handleSaveUpdate = async (
    apiUrl: string,
    apiPayload: any[],
    type: string,
    successMessage: string,
    navigationPath: string
  ) => {
    try {
      setLoading(true);
      const response =
        type === "Create"
          ? await postApi(apiUrl, apiPayload)
          : await putApi(apiUrl, apiPayload);
      if (response.success) {
        setMessage(successMessage, "success");
        if (navigationPath) {
          navigate(navigationPath);
        }
        fetchFilteredData(
          currentPage,
          selectStatus,
          selectedRoles,
          orderStatusSelected,
          orderTypeSelected,
          handlingUnitSelected,
          reportTypeSelected,
          reportCatSelected,
          reportDateTypeSelected,
          reportSchSelected,
          paymentStatusSelected,
          agentTypeSelected,
          hubSelected,
          serviceSelected,
          customerSelectedLevel,
          customerTypeLevel,
          agentUpdateStartDate,
          agentUpdateEndDate,
          dtsUpdateStartDate,
          dtsUpdateEndDate,
          createdStartDate,
          createdEndDate,
          invoiceFrom,
          invoiceTo,
          dueFrom,
          dueTo,
          orderDateFrom,
          orderDateTo,
          selectApiType,
          orderNumber,
          consigneeName,
          customerName,
          firstName,
          lastName,
          agentName,
          carrierCode,
          reportTitle,
          createdBy,
          invoiceGroup,
          invoiceEDI,
          invoiceEmail,
          invoiceNumber,
          orderRef,
          admin,
          service,
          vehicleType,
          packageType,
          hubName,
          templateName,
          templateForSelected,
          billingAccountCode,
          invoiceSettingSelected,
          city,
          state,
          zipCode,
          address,
          description,
          scac,
          carrier,
          region,
          stateOfRegion,
          onwardService,
          serviceName,
          agentEmail,
          subCustomerName,
          country,
          rateCharge,
          repName,
          repCustomerSelected,
          commissionTypeSelected,
          logisticsTypeSelected,
          moduleSelected,
        );
      } else {
        setMessage(response.error?.message || GENERAL_ERROR_MESSAGE, "error");
      }
    } catch (error) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggles the visibility of the filter modal.
   */
  const closeOpenfilterModal = useCallback(() => {
    setIsShowFilterModal(!isShowFilterModal);
  }, [isShowFilterModal]);

  const closeFilter = useCallback(() => {
    setIsShowFilterModal(false);
  }, []);

  // Filters are now persisted via redux-persist across all screens and refreshes.
  // No cleanup needed — each screen's filters are preserved independently by screenKey.

  useEffect(() => {
    if (isFirstRender) {
      // Only restore filter params if the user had actually applied them.
      // This prevents unapplied (in-progress) filter selections from being
      // auto-fetched when the user navigates back to the page.
      if (filterApplied) {
        fetchFilteredData(
          1,
          selectStatus,
          selectedRoles,
          orderStatusSelected,
          orderTypeSelected,
          handlingUnitSelected,
          reportTypeSelected,
          reportCatSelected,
          reportDateTypeSelected,
          reportSchSelected,
          paymentStatusSelected,
          agentTypeSelected,
          hubSelected,
          serviceSelected,
          customerSelectedLevel,
          customerTypeLevel,
          agentUpdateStartDate,
          agentUpdateEndDate,
          dtsUpdateStartDate,
          dtsUpdateEndDate,
          createdStartDate,
          createdEndDate,
          invoiceFrom,
          invoiceTo,
          dueFrom,
          dueTo,
          orderDateFrom,
          orderDateTo,
          selectApiType,
          orderNumber,
          consigneeName,
          customerName,
          firstName,
          lastName,
          agentName,
          carrierCode,
          reportTitle,
          createdBy,
          invoiceGroup,
          invoiceEDI,
          invoiceEmail,
          invoiceNumber,
          orderRef,
          admin,
          service,
          vehicleType,
          packageType,
          hubName,
          templateName,
          templateForSelected,
          billingAccountCode,
          invoiceSettingSelected,
          city,
          state,
          zipCode,
          address,
          description,
          scac,
          carrier,
          region,
          stateOfRegion,
          onwardService,
          serviceName,
          agentEmail,
          subCustomerName,
          country,
          rateCharge,
          repName,
          repCustomerSelected,
          commissionTypeSelected,
          logisticsTypeSelected,
          moduleSelected,
        );
      } else {
        fetchFilteredData(1);
      }
      setIsFirstRender(false);
    } else {
      const debounceFetch = setTimeout(() => {
        if (keyword) {
          // ⬇️ ONLY reset Redux page if this hook is actually bound to it
          if (bindToGlobalPage) {
            dispatch(setPage(1));
          }
          // Always fetch page 1 on a new keyword for this hook
          fetchFilteredData(1);
        } else {
          fetchFilteredData(
            currentPage,
            selectStatus,
            selectedRoles,
            orderStatusSelected,
            orderTypeSelected,
            handlingUnitSelected,
            reportTypeSelected,
            reportCatSelected,
            reportDateTypeSelected,
            reportSchSelected,
            paymentStatusSelected,
            agentTypeSelected,
            hubSelected,
            serviceSelected,
            customerSelectedLevel,
            customerTypeLevel,
            agentUpdateStartDate,
            agentUpdateEndDate,
            dtsUpdateStartDate,
            dtsUpdateEndDate,
            createdStartDate,
            createdEndDate,
            invoiceFrom,
            invoiceTo,
            dueFrom,
            dueTo,
            orderDateFrom,
            orderDateTo,
            selectApiType,
            orderNumber,
            consigneeName,
            customerName,
            firstName,
            lastName,
            agentName,
            carrierCode,
            reportTitle,
            createdBy,
            invoiceGroup,
            invoiceEDI,
            invoiceEmail,
            invoiceNumber,
            orderRef,
            admin,
            service,
            vehicleType,
            packageType,
            hubName,
            templateName,
            templateForSelected,
            billingAccountCode,
            invoiceSettingSelected,
            city,
            state,
            zipCode,
            address,
            description,
            scac,
            carrier,
            region,
            stateOfRegion,
            onwardService,
            serviceName,
            agentEmail,
            subCustomerName,
            country,
            rateCharge,
            repName,
            repCustomerSelected,
            commissionTypeSelected,
            logisticsTypeSelected,
            moduleSelected,
          );
        }
      }, 1000);

      return () => clearTimeout(debounceFetch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const captureAppliedFilters = () => {
    const snapshot = {
      selectStatus, selectedRoles, orderStatusSelected, orderTypeSelected,
      handlingUnitSelected, reportTypeSelected, reportCatSelected,
      reportDateTypeSelected, reportSchSelected, paymentStatusSelected, agentTypeSelected,
      hubSelected, serviceSelected, customerSelectedLevel, customerTypeLevel,
      agentUpdateStartDate, agentUpdateEndDate, dtsUpdateStartDate,
      dtsUpdateEndDate, createdStartDate, createdEndDate,
      invoiceFrom, invoiceTo, dueFrom, dueTo,
      orderDateFrom, orderDateTo, selectApiType, orderNumber,
      consigneeName, customerName, firstName, lastName, agentName, agentEmail,
      carrierCode, reportTitle, createdBy, invoiceGroup, invoiceEDI,
      invoiceEmail, invoiceNumber, orderRef, admin, service,
      vehicleType, packageType, hubName, templateName, templateForSelected,
      billingAccountCode, invoiceSettingSelected, city, state, zipCode,
      address, description, scac, carrier, region, stateOfRegion,
      onwardService, serviceName, subCustomerName, country, rateCharge,
      repName, repCustomerSelected, commissionTypeSelected,
      logisticsTypeSelected,
      moduleSelected,
    };
    lastAppliedFiltersRef.current = snapshot;
    // Persist the applied snapshot to Redux (only on explicit Apply action).
    // For pagination-only screens: save only paginationLimit, skip filter snapshot
    // and set filterApplied to false so filters aren't re-applied on next visit.
    if (screenKey) {
      dispatch(saveFilters({
        screenKey,
        filters: {
          ...(PAGINATION_ONLY_SCREENS.includes(screenKey) ? {} : snapshot),
          filterApplied: PAGINATION_ONLY_SCREENS.includes(screenKey) ? false : true,
          activeTab,
          dashboardStartDate: "", dashboardEndDate: "",
          dashboardLineStartDate: "", dashboardLineEndDate: "",
          dashboardLineType: "", dashboardOrderType: "",
          paginationLimit: reduxLimit ?? DEFAULT_PAGE_SIZE,
        } as ScreenFilterState,
      }));
    }
  };

  const fetchWithCurrentFilters = (page: number = currentPage) => {
    if (filterApplied && lastAppliedFiltersRef.current) {
      const s = lastAppliedFiltersRef.current;
      fetchFilteredData(
        page,
        s.selectStatus,
        s.selectedRoles,
        s.orderStatusSelected,
        s.orderTypeSelected,
        s.handlingUnitSelected,
        s.reportTypeSelected,
        s.reportCatSelected,
        s.reportDateTypeSelected,
        s.reportSchSelected,
        s.paymentStatusSelected,
        s.agentTypeSelected,
        s.hubSelected,
        s.serviceSelected,
        s.customerSelectedLevel,
        s.customerTypeLevel,
        s.agentUpdateStartDate,
        s.agentUpdateEndDate,
        s.dtsUpdateStartDate,
        s.dtsUpdateEndDate,
        s.createdStartDate,
        s.createdEndDate,
        s.invoiceFrom,
        s.invoiceTo,
        s.dueFrom,
        s.dueTo,
        s.orderDateFrom,
        s.orderDateTo,
        s.selectApiType,
        s.orderNumber,
        s.consigneeName,
        s.customerName,
        s.firstName,
        s.lastName,
        s.agentName,
        s.carrierCode,
        s.reportTitle,
        s.createdBy,
        s.invoiceGroup,
        s.invoiceEDI,
        s.invoiceEmail,
        s.invoiceNumber,
        s.orderRef,
        s.admin,
        s.service,
        s.vehicleType,
        s.packageType,
        s.hubName,
        s.templateName,
        s.templateForSelected,
        s.billingAccountCode,
        s.invoiceSettingSelected,
        s.city,
        s.state,
        s.zipCode,
        s.address,
        s.description,
        s.scac,
        s.carrier,
        s.region,
        s.stateOfRegion,
        s.onwardService,
        s.serviceName,
        s.agentEmail,
        s.subCustomerName,
        s.country,
        s.rateCharge,
        s.repName,
        s.repCustomerSelected,
        s.commissionTypeSelected,
        s.logisticsTypeSelected,
        s.moduleSelected,
      );
    } else {
      // No filter applied — fetch without any filter params so unapplied
      // in-progress selections don't leak into the re-fetch.
      fetchFilteredData(page);
    }
  };

  const refreshPage = () => {
    if (!isFirstRender) {
      fetchWithCurrentFilters();
    }
  };

  /**
   * Handles changes to the search keyword input and triggers a data fetch.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
   */
   useEffect(() => {
    const limitChanged = prevLimitRef.current !== reduxLimit;
    prevLimitRef.current = reduxLimit;
    if (!isFirstRender && bindToGlobalPage) {
      const pageToFetch = limitChanged ? 1 : currentPage;

      // If no filter is applied, always fetch clean with no params to prevent
      // in-progress (unapplied) filter selections from leaking into the fetch.
      // If a filter was applied, use the last-applied snapshot (if captured) so that
      // in-progress (unapplied) filter changes don't leak into the re-fetch.
      if (!filterApplied) {
        fetchFilteredData(pageToFetch);
      } else if (lastAppliedFiltersRef.current) {
        const s = lastAppliedFiltersRef.current;
        fetchFilteredData(
          pageToFetch,
          s.selectStatus, s.selectedRoles, s.orderStatusSelected, s.orderTypeSelected,
          s.handlingUnitSelected, s.reportTypeSelected, s.reportCatSelected,
          s.reportDateTypeSelected, s.reportSchSelected, s.paymentStatusSelected, s.agentTypeSelected,
          s.hubSelected, s.serviceSelected, s.customerSelectedLevel, s.customerTypeLevel,
          s.agentUpdateStartDate, s.agentUpdateEndDate, s.dtsUpdateStartDate,
          s.dtsUpdateEndDate, s.createdStartDate, s.createdEndDate,
          s.invoiceFrom, s.invoiceTo, s.dueFrom, s.dueTo,
          s.orderDateFrom, s.orderDateTo, s.selectApiType, s.orderNumber,
          s.consigneeName, s.customerName, s.firstName, s.lastName, s.agentName,
          s.carrierCode, s.reportTitle, s.createdBy, s.invoiceGroup, s.invoiceEDI,
          s.invoiceEmail, s.invoiceNumber, s.orderRef, s.admin, s.service,
          s.vehicleType, s.packageType, s.hubName, s.templateName, s.templateForSelected,
          s.billingAccountCode, s.invoiceSettingSelected, s.city, s.state, s.zipCode,
          s.address, s.description, s.scac, s.carrier, s.region, s.stateOfRegion,
          s.onwardService, s.serviceName, s.agentEmail, s.subCustomerName, s.country,
          s.rateCharge,
          s.repName, s.repCustomerSelected, s.commissionTypeSelected,
          s.logisticsTypeSelected,
          s.moduleSelected,
        );
      } else {
        fetchFilteredData(
          pageToFetch,
          selectStatus,
          selectedRoles,
          orderStatusSelected,
          orderTypeSelected,
          handlingUnitSelected,
          reportTypeSelected,
          reportCatSelected,
          reportDateTypeSelected,
          reportSchSelected,
          paymentStatusSelected,
          agentTypeSelected,
          hubSelected,
          serviceSelected,
          customerSelectedLevel,
          customerTypeLevel,
          agentUpdateStartDate,
          agentUpdateEndDate,
          dtsUpdateStartDate,
          dtsUpdateEndDate,
          createdStartDate,
          createdEndDate,
          invoiceFrom,
          invoiceTo,
          dueFrom,
          dueTo,
          orderDateFrom,
          orderDateTo,
          selectApiType,
          orderNumber,
          consigneeName,
          customerName,
          firstName,
          lastName,
          agentName,
          carrierCode,
          reportTitle,
          createdBy,
          invoiceGroup,
          invoiceEDI,
          invoiceEmail,
          invoiceNumber,
          orderRef,
          admin,
          service,
          vehicleType,
          packageType,
          hubName,
          templateName,
          templateForSelected,
          billingAccountCode,
          invoiceSettingSelected,
          city,
          state,
          zipCode,
          address,
          description,
          scac,
          carrier,
          region,
          stateOfRegion,
          onwardService,
          serviceName,
          agentEmail,
          subCustomerName,
          country,
          rateCharge,
          repName,
          repCustomerSelected,
          commissionTypeSelected,
          logisticsTypeSelected,
          moduleSelected,
        );
      }
    }
  }, [currentPage, bindToGlobalPage, reduxLimit]);



  // Filters are only persisted to Redux when the user explicitly clicks Apply
  // (via captureAppliedFilters) or Clear (via clearCurrentFilters).
  // No auto-save on filter state changes.

  // Persist pagination limit to Redux on change so it survives navigation.
  useEffect(() => {
    if (!screenKey || isFirstRender) return;
    const { savedFilters } = filterState;
    const existing = savedFilters?.[screenKey];
    dispatch(saveFilters({
      screenKey,
      filters: {
        ...(existing || {}),
        paginationLimit: reduxLimit ?? DEFAULT_PAGE_SIZE,
      } as ScreenFilterState,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxLimit]);

  // Clear the applied snapshot when the filter is turned off so stale values
  // don't leak into subsequent saves.
  useEffect(() => {
    if (!filterApplied) {
      lastAppliedFiltersRef.current = null;
    }
  }, [filterApplied]);

  const clearCurrentFilters = useCallback(() => {
    if (!screenKey) return;
    dispatch(clearFilters(screenKey));
  }, [screenKey, dispatch]);


  return {
    data,
    setData,
    keyword,
    currentPage,
    loading,
    setLoading,
    handleKeywordChange,
    fetchFilteredData,
    handleSaveUpdate,
    isShowFilterModal,
    setIsShowFilterModal,
    closeOpenfilterModal,
    closeFilter,
    selectStatus,
    setSelectStatus,
    selectApiType,
    setSelectApiType,
    selectedRoles,
    setSelectedRoles,
    rolesDropdownData,
    setRolesDropdownData,
    orderStatusSelected,
    setOrderStatusSelected,
    orderStatusDropdownData,
    setOrderStatusDropdownData,
    serviceLevelDropdownData,
    setServiceLevelDropdownData,
    orderTypeSelected,
    setOrderTypeSelected,
    handlingUnitSelected,
    setHandlingUnitSelected,
    orderTypeDropdownData,
    setOrderTypeDropdownData,
    handlingUnitDropDown,
    setHandlingUnitDropDown,
    agentTypeSelected,
    setAgentTypeSelected,
    agentTypeDropdownData,
    setAgentTypeDropdownData,
    hubSelected,
    setHubSelected,
    hubDropdownData,
    setHubDropdownData,
    serviceSelected,
    setServiceSelected,
    serviceDropdownData,
    setServiceDropdownData,
    agentUpdateStartDate,
    setAgentUpdateStartDate,
    agentUpdateEndDate,
    setAgentUpdateEndDate,
    dtsUpdateStartDate,
    setDtsStartDate,
    dtsUpdateEndDate,
    setDtsEndDate,
    invoiceFrom,
    setInvoiceFrom,
    invoiceTo,
    setInvoiceTo,
    dueFrom,
    setDueFrom,
    dueTo,
    setDueTo,
    orderDateFrom,
    setOrderDateFrom,
    orderDateTo,
    setOrderDateTo,
    customerSelectedLevel,
    setCustomerSelectedLevel,
    customerLevelDropdownData,
    setCustomerLevelDropdownData,
    customerTypeLevel,
    setCustomerTypeLevel,
    refreshPage,
    fetchWithCurrentFilters,
    captureAppliedFilters,
    filterApplied,
    setFilterApplied,
    reportSchSelected,
    setReportSchSelected,
    reportSchDropdownData,
    setReportSchDropDownData,
    reportTypeSelected,
    setReportTypeSelected,
    reportTypeDropdownData,
    setReportTypeDropDownData,
    reportCatSelected,
    setReportCatSelected,
    reportCatDropdownData,
    setReportCatDropDownData,
    reportDateTypeSelected,
    setReportDateTypeSelected,
    reportDateTypeDropdownData,
    setReportDateTypeDropDownData,
    paymentStatusSelected,
    setPaymentStatusSelected,
    paymentStatusDropdownData,
    setPaymentStatusDropDownData,
    createdStartDate,
    setCreatedStartDate,
    createdEndDate,
    setCreatedEndDate,
    consigneeName,
    setConsigneeName,
    customerName,
    setCustomerName,
    orderNumber,
    setOrderNumber,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    agentName,
    setAgentName,
    agentEmail,
    setAgentEmail,
    carrierCode,
    setCarrierCode,
    reportTitle,
    setReportTitle,
    createdBy,
    setCreatedBy,
    invoiceGroup,
    setInvoiceGroup,
    invoiceEDI,
    setInvoiceEDI,
    invoiceEmail,
    setInvoiceEmail,
    invoiceNumber,
    setInvoiceNumber,
    orderRef,
    setOrderRef,
    admin,
    setAdmin,
    service,
    setService,
    vehicleType,
    setVehicleType,
    packageType,
    setPackageType,
    hubName,
    setHubName,
    templateName,
    setTemplateName,
    templateForSelected,
    setTemplateForSelected,
    templateForDropdownData,
    setTemplateForDropdownData,
    billingAccountCode,
    setBillingAccountCode,
    invoiceSettingSelected,
    setInvoiceSettingSelected,
    invoiceSettingDropdownData,
    setInvoiceSettingDropDownData,
    city,
    state,
    zipCode,
    setCity,
    setState,
    setZipCode,
    address,
    setAddress,
    description,
    setDescription,
    scac,
    setScac,
    carrier,
    setCarrier,
    setKeywordValue,
    region,
    setRegion,
    stateOfRegion,
    setStateOfRegion,
    serviceName,
    setServiceName,
    onwardService,
    setOnwardService,
    subCustomerName,
    setSubCustomerName,
    country,
    setCountry,
    rateCharge,
    setRateCharge,
    repName,
    setRepName,
    repCustomerSelected,
    setRepCustomerSelected,
    repCustomerDropdownData,
    setRepCustomerDropdownData,
    commissionTypeSelected,
    setCommissionTypeSelected,
    logisticsTypeSelected,
    setLogisticsTypeSelected,
    moduleSelected,
    setModuleSelected,
    moduleDropdownData,
    setModuleDropdownData,
    commissionTypeDropdownData,
    setCommissionTypeDropdownData,
    clearCurrentFilters,
    restoredActiveTab: restoredFilters?.activeTab,
  };
};

export default useSearchAndFilter;
